import React, { useState, useEffect, useRef } from 'react';
import axios from '../../api/axios';
import Layout from '../../common/components/Navbar';
import SOATemplate from '../../common/components/SOATemplate';
// Removed react-to-print import
import {
  Box,
  Title,
  Paper,
  Table,
  Badge,
  Grid,
  Card,
  Divider,
  Button,
  Modal,
  TextInput,
  Select,
  Text,
  Group,
  Alert,
  Image,
  ActionIcon,
  FileInput,
  ScrollArea,
  Stack,
  Loader,
  SimpleGrid,
  Tabs,
  Collapse
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { 
  IconWallet, 
  IconReceipt, 
  IconHistory, 
  IconUpload, 
  IconPhoto, 
  IconX,
  IconTag,
  IconCheck,
  IconMapPin,
  IconFileText,
  IconPrinter,
  IconQrcode 
} from '@tabler/icons-react';

// IMPORTANT: Make sure you saved your QR image as 'gcash_qr.jpg' in client/src/assets/
import gcashQr from '../../assets/gcash_qr.jpg'; 

const ResidentDashboard = () => {
  const [invoices, setInvoices] = useState([]);
  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(true);

  // Resident Profile Data
  const [residentProfile, setResidentProfile] = useState(null);

  // SOA Data
  const [soaData, setSoaData] = useState(null);
  const [openSoaModal, setOpenSoaModal] = useState(false);

  // Payment Modal
  const [openPayDialog, setOpenPayDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: 'gcash',
    referenceNumber: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showQr, setShowQr] = useState(false); // State for QR Toggle

  // History Modal
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);

  // Settings (Prices & Venues)
  const [prices, setPrices] = useState({ monthly_dues: 0, sticker_fee: 0 });
  const [venueList, setVenueList] = useState([]);

  // Ref for the content we want to print
  const componentRef = useRef();

  // --- BLOB URL PRINTING LOGIC ---
  const handlePrintBlob = () => {
    const content = componentRef.current;
    if (!content) {
        notifications.show({ title: 'Error', message: 'Nothing to print.', color: 'red' });
        return;
    }

    // 1. Get the HTML of the SOA Template
    const htmlContent = content.innerHTML;

    // 2. Get all styles from the current document (Mantine styles, local CSS)
    // This ensures the new window looks exactly like the modal
    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
        .map(node => node.outerHTML)
        .join('');

    // 3. Construct the Full HTML File
    const blobContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Statement of Account - ${user.name || 'Resident'}</title>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            ${styles}
            <style>
                @page { 
                    size: A4; 
                    margin: 10mm; 
                }
                body {
                    background-color: white;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                    margin: 0;
                    padding: 20px;
                    display: flex;
                    justify-content: center;
                }
                /* Ensure the paper container doesn't overflow in print */
                .soa-container {
                    width: 100% !important;
                    max-width: 210mm !important; 
                    box-shadow: none !important;
                    border: none !important;
                }
            </style>
        </head>
        <body>
            ${htmlContent}
            <script>
                // Automatically print when the blob opens, then close (optional)
                window.onload = () => {
                    setTimeout(() => {
                        window.print();
                    }, 500);
                };
            </script>
        </body>
        </html>
    `;

    // 4. Create the Blob
    const blob = new Blob([blobContent], { type: 'text/html' });
    
    // 5. Create URL and Open
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  const formatMoney = (amount) => {
    return Number(amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (userData) {
      setUser(userData);
      loadDashboardData(userData);
    }
  }, []);

  const loadDashboardData = async (userData) => {
      setLoading(true);
      await Promise.all([
          fetchMyInvoices(userData),
          fetchSettings(userData),
          fetchSOAData(userData),
          fetchResidentProfile(userData)
      ]);
      setLoading(false);
  };

  const fetchResidentProfile = async (userData) => {
      try {
          if (!userData.linkedResident) return;
          const config = { headers: { Authorization: `Bearer ${userData.token}` } };
          const { data } = await axios.get(`/api/residents/${userData.linkedResident}`, config);
          setResidentProfile(data);
      } catch (error) {
          console.error("Failed to load resident profile", error);
      }
  };

  const fetchMyInvoices = async (userData) => {
    try {
      const config = { headers: { Authorization: `Bearer ${userData.token}` } };
      const { data } = await axios.get('/api/billing/my-invoices', config);
      setInvoices(data);
    } catch (error) {
      console.error("Failed to load invoices");
    }
  };

  const fetchSettings = async (userData) => {
      try {
        const config = { headers: { Authorization: `Bearer ${userData.token}` } };
        const { data } = await axios.get('/api/billing/settings', config);
        const settingsObj = {};
        
        data.forEach(s => {
            if (s.type === 'venue_list') {
                const list = s.value || [];
                const normalizedList = list.map(v => (typeof v === 'string' ? { name: v, price: 0 } : v));
                setVenueList(normalizedList);
            } else {
                settingsObj[s.type] = s.amount;
            }
        });
        setPrices(settingsObj);
      } catch (error) {
        console.error("Failed to load prices");
      }
  };

  const fetchSOAData = async (userData) => {
      try {
          if (!userData.linkedResident) return;
          const config = { headers: { Authorization: `Bearer ${userData.token}` } };
          const { data } = await axios.get(`/api/billing/soa-data/${userData.linkedResident}`, config);
          setSoaData(data);
      } catch (error) {
          console.error("Failed to load SOA data", error);
      }
  };

  const handlePayClick = (invoice) => {
    setSelectedInvoice(invoice);
    setOpenPayDialog(true);
    setShowQr(false); // Reset QR view when opening
  };

  const handleViewHistory = async (invoice) => {
    setSelectedInvoice(invoice);
    try {
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      const { data } = await axios.get(`/api/billing/history/${invoice._id}`, config);
      setHistoryData(data);
      setOpenHistoryDialog(true);
    } catch (error) {
      notifications.show({ title: 'Error', message: "Could not fetch history", color: 'red' });
    }
  };

  const handleFileChange = (files) => {
    if (files.length > 3) {
        notifications.show({ title: 'Error', message: "You can only upload up to 3 images.", color: 'red' });
      return;
    }
    setSelectedFiles(files);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'referenceNumber') {
      const rawValue = value.replace(/\D/g, '');
      const limitedValue = rawValue.slice(0, 13);
      let formattedValue = limitedValue;
      if (limitedValue.length > 4) {
        formattedValue = `${limitedValue.slice(0, 4)} ${limitedValue.slice(4)}`;
      }
      if (limitedValue.length > 7) {
        formattedValue = `${limitedValue.slice(0, 4)} ${limitedValue.slice(4, 7)} ${limitedValue.slice(7)}`;
      }
      setPaymentForm({ ...paymentForm, [name]: formattedValue });
    } else {
      setPaymentForm({ ...paymentForm, [name]: value });
    }
  };

  const handleSelectChange = (name, value) => {
    setPaymentForm({ ...paymentForm, [name]: value });
    if (name === 'paymentMethod' && value !== 'gcash') {
        setShowQr(false); // Hide QR if they switch away from Gcash
    }
  };

  const handleSubmitPayment = async () => {
    if (!paymentForm.paymentMethod) {
        notifications.show({ title: 'Error', message: "Please select a payment method.", color: 'red' });
        return;
    }

    const rawRefNum = paymentForm.referenceNumber.replace(/\s/g, '');
    if (!rawRefNum) {
        notifications.show({ title: 'Error', message: "Please enter the reference number.", color: 'red' });
        return;
    }
    if (rawRefNum.length !== 13) {
        notifications.show({ title: 'Error', message: "Reference number must be exactly 13 digits.", color: 'red' });
        return;
    }

    if (selectedFiles.length === 0) {
        notifications.show({ title: 'Warning', message: "Please upload at least one screenshot.", color: 'yellow' });
      return;
    }

    try {
      const config = { 
        headers: { 
          Authorization: `Bearer ${user.token}`,
          'Content-Type': 'multipart/form-data'
        } 
      };

      const formData = new FormData();
      formData.append('invoiceId', selectedInvoice._id);
      formData.append('paymentMethod', paymentForm.paymentMethod);
      formData.append('referenceNumber', paymentForm.referenceNumber);
      
      selectedFiles.forEach((file) => {
        formData.append('receiptImages', file);
      });

      await axios.post('/api/billing/pay/online', formData, config);

      notifications.show({ title: 'Success', message: "Payment submitted! Waiting for Admin approval.", color: 'green' });
      
      setOpenPayDialog(false);
      setPaymentForm({ paymentMethod: 'gcash', referenceNumber: '' });
      setSelectedFiles([]);
      loadDashboardData(user); 

    } catch (error) {
      console.error(error);
      notifications.show({ title: 'Error', message: error.response?.data?.message || "Error submitting payment.", color: 'red' });
    }
  };

  const getSafeTotal = (inv) => inv?.totalAmount || inv?.amount || 0;
  const getSafePaid = (inv) => inv?.amountPaid || 0;

  const totalUnpaid = invoices.reduce((acc, curr) => {
    if (curr.status !== 'paid') {
        return acc + (getSafeTotal(curr) - getSafePaid(curr));
    }
    return acc;
  }, 0);

  const prevSoaList = invoices.filter(inv => inv.type === 'monthly_dues' && inv._id !== soaData?.currentInvoiceId);

  return (
    <Layout>
      <Box mb="xl">
        <Title order={2} style={{ fontSize: '1.5rem' }}>Welcome, {user.name}</Title>
      </Box>

      {loading ? (
          <Group justify="center" py="xl"><Loader size="lg" /></Group>
      ) : (
        <>
            {/* ROW 1: BALANCE & FEES */}
            <Grid mb="xl">
                <Grid.Col span={{ base: 12, md: 6 }}>
                    <Card 
                        shadow="sm" 
                        p="lg" 
                        radius="md" 
                        withBorder 
                        style={{ 
                            backgroundColor: totalUnpaid > 0 ? '#d32f2f' : '#2e7d32', 
                            color: 'white', 
                            height: '100%' 
                        }}
                    >
                        <Group align="center" wrap="nowrap">
                        {totalUnpaid > 0 ? <IconWallet size={48} /> : <IconCheck size={48} />}
                        <Box>
                            <Text size="sm" fw={500}>Total Outstanding Balance</Text>
                            <Title order={1} style={{ fontSize: '2rem' }}>₱{formatMoney(totalUnpaid)}</Title>
                            {totalUnpaid === 0 && <Text size="xs" style={{ opacity: 0.8 }}>You are in good standing!</Text>}
                        </Box>
                        </Group>
                    </Card>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 6 }}>
                    <Card shadow="sm" padding="lg" radius="md" withBorder style={{ height: '100%' }}>
                        <Text fz="lg" fw={600} mb="md">Current Payable Prices</Text>
                        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
                            <Card withBorder p="xs" radius="md">
                                <Group>
                                    <IconTag size={24} color="teal" />
                                    <Box>
                                        <Text size="xs" c="dimmed">Monthly Dues</Text>
                                        <Text fw={700}>₱{prices.monthly_dues || 0}</Text>
                                    </Box>
                                </Group>
                            </Card>
                            <Card withBorder p="xs" radius="md">
                                <Group>
                                    <IconTag size={24} color="blue" />
                                    <Box>
                                        <Text size="xs" c="dimmed">Sticker Fee</Text>
                                        <Text fw={700}>₱{prices.sticker_fee || 0}</Text>
                                    </Box>
                                </Group>
                            </Card>
                            {venueList && venueList.length > 0 ? (
                                venueList.map((venue, index) => (
                                    <Card withBorder p="xs" radius="md" key={index}>
                                        <Group>
                                            <IconMapPin size={24} color="orange" />
                                            <Box>
                                                <Text size="xs" c="dimmed" lineClamp={1}>{venue.name}</Text>
                                                <Text fw={700}>₱{venue.price}</Text>
                                            </Box>
                                        </Group>
                                    </Card>
                                ))
                            ) : (
                                <Card withBorder p="xs" radius="md">
                                    <Group>
                                        <IconMapPin size={24} color="orange" />
                                        <Box>
                                            <Text size="xs" c="dimmed">Venues</Text>
                                            <Text fw={700} c="dimmed" size="xs">None Listed</Text>
                                        </Box>
                                    </Group>
                                </Card>
                            )}
                        </SimpleGrid>
                    </Card>
                </Grid.Col>
            </Grid>

            {/* ROW 2: STATEMENT OF ACCOUNT */}
            <Box mb="xl">
                <Card shadow="sm" p="lg" radius="md" withBorder style={{ borderTop: '4px solid #1971c2' }}>
                    <Group justify="space-between" mb="md">
                        <Group>
                            <IconFileText size={28} color="#1971c2" />
                            <Title order={3}>Statement of Account</Title>
                        </Group>
                        <Badge size="lg" color="blue">{soaData?.billingPeriod || 'Current Period'}</Badge>
                    </Group>
                    
                    <Divider mb="md" />

                    <Grid>
                        <Grid.Col span={6}>
                            <Text size="sm" c="dimmed">Current Charges</Text>
                            <Text fw={500} size="lg">₱{formatMoney(soaData?.currentCharges || 0)}</Text>
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <Text size="sm" c="dimmed">Previous Unpaid Amount</Text>
                            <Text fw={500} size="lg" c="red">₱{formatMoney(soaData?.previousUnpaidAmount || 0)}</Text>
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <Text size="sm" c="dimmed">Other Fees</Text>
                            <Text fw={500} size="lg">₱0.00</Text>
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <Text size="sm" c="dimmed">Total Amount Due</Text>
                            <Title order={2} c="blue">₱{formatMoney(soaData?.totalAmountDue || 0)}</Title>
                        </Grid.Col>
                    </Grid>

                    <Divider my="md" />

                    <Group justify="space-between" align="center">
                        <Stack gap={0}>
                            <Text size="xs" c="dimmed">Due Date: {soaData?.dueDate ? new Date(soaData.dueDate).toLocaleDateString() : 'N/A'}</Text>
                            <Text size="xs" c="dimmed" fs="italic">Note: Generated on {new Date().toLocaleDateString()}. Visit office for inquiries.</Text>
                        </Stack>
                        
                        <Group>
                            <Button variant="default" leftSection={<IconFileText size={16} />} onClick={() => setOpenSoaModal(true)}>
                                View Document
                            </Button>
                            
                            <Button 
                                onClick={() => {
                                    if(soaData?.currentInvoiceId) {
                                        const inv = invoices.find(i => i._id === soaData.currentInvoiceId);
                                        if(inv) handlePayClick(inv);
                                        else notifications.show({ message: 'Invoice detail not found.', color: 'red' });
                                    } else {
                                        notifications.show({ message: 'No current invoice to pay.', color: 'orange' });
                                    }
                                }}
                                disabled={!soaData?.currentInvoiceId || soaData.totalAmountDue <= 0}
                            >
                                Pay Online
                            </Button>
                        </Group>
                    </Group>
                </Card>
            </Box>

            {/* ROW 3: TABS SECTION */}
            <Paper p="md" shadow="sm" withBorder>
                <Tabs defaultValue="invoices">
                    <Tabs.List>
                        <Tabs.Tab value="prev_soa" leftSection={<IconHistory size={16} />}>
                            Previous Statement of Accounts
                        </Tabs.Tab>
                        <Tabs.Tab value="invoices" leftSection={<IconReceipt size={16} />}>
                            My Invoices
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="prev_soa" pt="md">
                        {prevSoaList.length === 0 ? (
                            <Text c="dimmed" align="center" py="xl">No previous statements found.</Text>
                        ) : (
                            <Table striped highlightOnHover>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Billing Period</Table.Th>
                                        <Table.Th>Generated Date</Table.Th>
                                        <Table.Th>Action</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {prevSoaList.map((inv) => (
                                        <Table.Tr key={inv._id}>
                                            <Table.Td>{inv.billingPeriod || 'N/A'}</Table.Td>
                                            <Table.Td>{new Date(inv.createdAt).toLocaleDateString()}</Table.Td>
                                            <Table.Td>
                                                <Button 
                                                    variant="subtle" 
                                                    size="xs" 
                                                    leftSection={<IconFileText size={14} />}
                                                    onClick={() => notifications.show({ message: 'Historical PDF view requires archive implementation.', color: 'blue' })}
                                                >
                                                    View
                                                </Button>
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        )}
                    </Tabs.Panel>

                    <Tabs.Panel value="invoices" pt="md">
                        <Grid>
                        {invoices.map((invoice) => {
                            const total = getSafeTotal(invoice);
                            const paid = getSafePaid(invoice);
                            const remaining = total - paid;

                            return (
                                <Grid.Col span={{ base: 12, md: 6 }} key={invoice._id}>
                                <Paper p="sm" shadow="xs" withBorder style={{ borderLeft: invoice.status === 'paid' ? '4px solid #2e7d32' : '4px solid #d32f2f' }}>
                                    <Stack gap="xs">
                                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                                            <Box>
                                                <Text fw={600} lineClamp={1}>{invoice.description}</Text>
                                                <Text size="xs" c="dimmed">
                                                    Due: {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}
                                                </Text>
                                            </Box>
                                            <Badge size="sm" color={invoice.status === 'paid' ? 'green' : invoice.status === 'pending_approval' ? 'orange' : 'red'}>
                                                {invoice.status === 'pending_approval' ? 'VERIFYING' : invoice.status.toUpperCase()}
                                            </Badge>
                                        </Group>
                                        <Group justify="space-between">
                                            <Text size="sm">Due: ₱{formatMoney(total)}</Text>
                                            <Text size="sm" c="green">Paid: ₱{formatMoney(paid)}</Text>
                                        </Group>
                                        <Group grow mt="xs">
                                            {invoice.status !== 'paid' && invoice.status !== 'pending_approval' && remaining > 0 && (
                                                <Button size="xs" onClick={() => handlePayClick(invoice)}>Pay</Button>
                                            )}
                                            <Button size="xs" variant="default" onClick={() => handleViewHistory(invoice)}>History</Button>
                                        </Group>
                                    </Stack>
                                </Paper>
                                </Grid.Col>
                            );
                        })}
                        </Grid>
                    </Tabs.Panel>
                </Tabs>
            </Paper>
        </>
      )}

      {/* ONLINE PAYMENT MODAL */}
      <Modal opened={openPayDialog} onClose={() => setOpenPayDialog(false)} title="Upload Payment Proof" size="md" centered>
        <Stack>
            <Alert icon={<IconWallet size={16} />} title="Instruction" color="blue">
              Please transfer <strong>₱{formatMoney(getSafeTotal(selectedInvoice) - getSafePaid(selectedInvoice))}</strong> to the Association Bank/GCash.
            </Alert>
            <Select 
                label="Payment Method" 
                withAsterisk
                value={paymentForm.paymentMethod} 
                onChange={(val) => handleSelectChange('paymentMethod', val)} 
                data={[
                    { value: 'gcash', label: 'GCash' }, 
                    { value: 'bank_transfer', label: 'Bank Transfer' }
                ]} 
                allowDeselect={false} 
            />

            {paymentForm.paymentMethod === 'gcash' && (
                <Box>
                    <Button 
                        variant="light" 
                        color="blue" 
                        fullWidth 
                        mt="xs" 
                        leftSection={<IconQrcode size={16} />}
                        onClick={() => setShowQr(!showQr)}
                    >
                        {showQr ? 'Hide GCash QR' : 'Show GCash QR Code'}
                    </Button>
                    <Collapse in={showQr}>
                        <Box mt="sm" style={{ textAlign: 'center', border: '1px dashed #ced4da', borderRadius: '8px', padding: '10px' }}>
                            <Text size="sm" c="dimmed" mb={5}>Scan using GCash App</Text>
                            <Image src={gcashQr} w={220} mx="auto" radius="md" />
                        </Box>
                    </Collapse>
                </Box>
            )}

            <TextInput 
                label="Reference Number (Ref No.)" 
                name="referenceNumber" 
                withAsterisk
                placeholder="XXXX XXX XXXXXX"
                value={paymentForm.referenceNumber} 
                onChange={handleInputChange} 
                maxLength={15}
                description="Must be 13 digits (e.g., 1234 567 890123)"
            />
            <FileInput 
                label="Upload Screenshots (Max 3)" 
                withAsterisk
                multiple 
                accept="image/*" 
                onChange={handleFileChange} 
                value={selectedFiles} 
                leftSection={<IconUpload size={16} />} 
            />
            <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={() => setOpenPayDialog(false)}>Cancel</Button>
                <Button onClick={handleSubmitPayment}>Submit</Button>
            </Group>
        </Stack>
      </Modal>

      {/* SOA DOCUMENT MODAL */}
      <Modal opened={openSoaModal} onClose={() => setOpenSoaModal(false)} title="Document View" size="auto" centered>
        <Box>
            <div ref={componentRef}>
                {soaData && <SOATemplate data={soaData} resident={residentProfile || user} />}
            </div>
            <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={() => setOpenSoaModal(false)}>Close</Button>
                {/* Updated Print Button triggers the Blob Logic */}
                <Button leftSection={<IconPrinter size={18} />} onClick={handlePrintBlob}>Print</Button>
            </Group>
        </Box>
      </Modal>

      {/* HISTORY & IMAGE PREVIEW MODALS */}
      <Modal opened={openHistoryDialog} onClose={() => setOpenHistoryDialog(false)} title="Payment History" size="lg" centered scrollAreaComponent={ScrollArea.Autosize}>
        <Text size="sm" mb="md">Invoice: <strong>{selectedInvoice?.description}</strong></Text>
        <Paper withBorder>
            <ScrollArea>
                <Table striped minWidth={500}>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Date</Table.Th>
                            <Table.Th>Amount</Table.Th>
                            <Table.Th>Method</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Proof</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {historyData.length === 0 ? (
                            <Table.Tr><Table.Td colSpan={5} align="center">No transactions found</Table.Td></Table.Tr>
                        ) : (
                            historyData.map((tx) => (
                                <Table.Tr key={tx._id}>
                                    <Table.Td>{new Date(tx.paymentDate).toLocaleDateString()}</Table.Td>
                                    <Table.Td>₱{formatMoney(tx.amountPaid)}</Table.Td>
                                    <Table.Td style={{ textTransform: 'capitalize' }}>{tx.paymentMethod}</Table.Td>
                                    <Table.Td><Badge color={tx.status === 'completed' ? 'green' : 'red'}>{tx.status}</Badge></Table.Td>
                                    <Table.Td>
                                        <Group gap="xs">
                                            {tx.receiptImages?.map((img, i) => (
                                                <Image key={i} src={img} w={30} h={30} radius="sm" onClick={() => setImagePreview(img)} style={{cursor: 'pointer'}} />
                                            ))}
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            ))
                        )}
                    </Table.Tbody>
                </Table>
            </ScrollArea>
        </Paper>
      </Modal>

      <Modal opened={!!imagePreview} onClose={() => setImagePreview(null)} size="xl" withCloseButton={false} centered styles={{ body: { padding: 0 }, content: { backgroundColor: 'transparent', boxShadow: 'none' } }}>
        <Box pos="relative">
            <ActionIcon onClick={() => setImagePreview(null)} color="gray" variant="filled" radius="xl" style={{ position: 'absolute', right: 10, top: 10, zIndex: 10 }}><IconX size={20} /></ActionIcon>
            <Image src={imagePreview} radius="md" style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
        </Box>
      </Modal>

    </Layout>
  );
};

export default ResidentDashboard;